import React, { useMemo, useState } from "react";
import api from "../api/api";
import moment from "moment-timezone";
import "moment/locale/es";
import "../styles/historial-pedidos.css";
import { useNavigate } from "react-router-dom";

/**
 * Historial de pedidos (aislado con prefijo hp-)
 * - No carga datos al montar.
 * - Busca por texto (cliente o número) y/o por fecha de creación (día completo en America/Costa_Rica).
 * - Filtra en el cliente a partir de GET /api/orders (router actual).
 * - Muestra resultados y modal con detalle (OR- vs F2000-).
 */
export default function HistorialPedidos() {
  const navigate = useNavigate();

  // UI state
  const [hpQuery, setHpQuery] = useState("");
  const [hpDate, setHpDate] = useState(""); // "YYYY-MM-DD" (input date)
  const [hpLoading, setHpLoading] = useState(false);
  const [hpOrders, setHpOrders] = useState([]);
  const [hpSelected, setHpSelected] = useState(null);
  const [hpError, setHpError] = useState("");

  const HP_TZ = "America/Costa_Rica";

  const hpHasFilters = useMemo(() => {
    const q = (hpQuery || "").trim();
    const d = (hpDate || "").trim();
    return q !== "" || d !== "";
  }, [hpQuery, hpDate]);

  const hpBuildDateRange = () => {
    if (!hpDate) return {};
    const start = moment.tz(hpDate, HP_TZ).startOf("day");
    const end = moment.tz(hpDate, HP_TZ).endOf("day");
    return { start, end };
  };

  const hpHandleSearch = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    if (!hpHasFilters) {
      setHpOrders([]);
      setHpError("Escribe un cliente / número o selecciona una fecha.");
      return;
    }

    setHpLoading(true);
    setHpError("");

    try {
      // ✅ Con JWT: usamos api (manda Authorization: Bearer ...)
      const res = await api.get("/api/orders");
      const all = Array.isArray(res.data) ? res.data : [];

      const q = (hpQuery || "").trim().toLowerCase();
      const { start, end } = hpBuildDateRange();

      const filtered = all.filter((o) => {
        // Texto (cliente o número)
        const matchText =
          q === "" ||
          (o.orderNumber || "").toLowerCase().includes(q) ||
          (o.customer || "").toLowerCase().includes(q);

        // Fecha de creación (createdAt) dentro del día CR
        let matchDate = true;
        if (start && end) {
          if (!o.createdAt) matchDate = false;
          else {
            const created = moment(o.createdAt).tz(HP_TZ);
            matchDate = created.isBetween(start, end, undefined, "[]");
          }
        }

        return matchText && matchDate;
      });

      setHpOrders(filtered);
    } catch (err) {
      console.error(err);

      if (err?.response?.status === 401) {
        // Token inválido/expirado
        localStorage.removeItem("token");
        navigate("/"); // cambia a "/login" si tu login está ahí
        return;
      }

      setHpError("No se pudo obtener el historial. Intenta de nuevo.");
      setHpOrders([]);
    } finally {
      setHpLoading(false);
    }
  };

  const hpCloseModal = () => setHpSelected(null);

  const hpMoney = new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
  });

  return (
    <div className="hp-page">
      <header className="hp-header">
        <h1 className="hp-title">Historial de pedidos</h1>

        <form className="hp-search" onSubmit={hpHandleSearch}>
          <input
            type="text"
            className="hp-input"
            placeholder="Buscar por cliente o número de orden…"
            value={hpQuery}
            onChange={(e) => setHpQuery(e.target.value)}
          />

          <div className="hp-date">
            <label className="hp-date-label" htmlFor="hp-date-input">
              Creados el:
            </label>
            <input
              id="hp-date-input"
              type="date"
              className="hp-date-input"
              value={hpDate}
              onChange={(e) => setHpDate(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="hp-search-btn"
            disabled={hpLoading}
            aria-label="Buscar pedidos"
          >
            {hpLoading ? "Buscando…" : "Buscar"}
          </button>
        </form>

        <p className="hp-hint">
          Escribe parte del <strong>cliente</strong> o del{" "}
          <strong>número de orden</strong>, o elige una <strong>fecha</strong>.
        </p>
      </header>

      {hpError && <div className="hp-error">{hpError}</div>}

      {!hpLoading && hpOrders.length > 0 && (
        <div className="hp-results">
          <table className="hp-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Cliente</th>
                <th>Para</th>
                <th>Creado</th>
                <th>Total</th>
                <th className="hp-only-desktop">Fuente</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {hpOrders.map((o) => (
                <tr key={o.id}>
                  <td>{o.orderNumber}</td>
                  <td>{o.customer || "—"}</td>
                  <td>{o.para || "—"}</td>
                  <td>
                    {o.createdAt
                      ? moment(o.createdAt).tz(HP_TZ).format("DD/MM/YYYY h:mma")
                      : "—"}
                  </td>
                  <td>
                    {o.totalPayment != null ? hpMoney.format(o.totalPayment) : "—"}
                  </td>
                  <td className="hp-only-desktop">{o.fuente || "—"}</td>
                  <td>
                    <button
                      className="hp-detail-btn"
                      onClick={() => setHpSelected(o)}
                    >
                      Ver detalle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hpOrders.length >= 1000 && (
            <p className="hp-note">
              Muchos resultados. Refina la búsqueda para acotar mejor.
            </p>
          )}
        </div>
      )}

      {/* Modal Detalle */}
      {hpSelected && (
        <div className="hp-modal-overlay" onClick={hpCloseModal}>
          <div
            className="hp-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="hp-modal-header">
              <h2 className="hp-modal-title">Detalle del Pedido</h2>
              <button className="hp-close" onClick={hpCloseModal} aria-label="Cerrar">
                ×
              </button>
            </div>

            <div className="hp-modal-body">
              {hpSelected.orderNumber?.startsWith("OR-") ? (
                <div className="hp-or">
                  <p><strong>Número:</strong> {hpSelected.orderNumber}</p>
                  <p><strong>Ejecutiv@:</strong> {hpSelected.ejecutivo || "N/A"}</p>
                  <p><strong>Cliente:</strong> {hpSelected.customer || "N/A"}</p>
                  <p><strong>Para:</strong> {hpSelected.para || "N/A"}</p>
                  <p><strong>Monto base:</strong> {hpSelected.total != null ? hpMoney.format(hpSelected.total) : "—"}</p>
                  <p><strong>IVA:</strong> {hpSelected.iva || "No"}</p>
                  <p><strong>Total:</strong> {hpSelected.totalPayment != null ? hpMoney.format(hpSelected.totalPayment) : "—"}</p>
                  <p><strong>Fecha y Hora:</strong> {hpSelected.scheduledTime ? moment(hpSelected.scheduledTime).tz(HP_TZ).format("DD/MM/YYYY h:mma") : "N/A"}</p>
                  <p><strong>Detalle:</strong> {hpSelected.detalleEntrega || hpSelected.orderItems || "N/A"}</p>
                  <p><strong>Recibido por:</strong> {hpSelected.recibidoPor || "N/A"}</p>
                  <p><strong>Forma de Pago:</strong> {hpSelected.formaPago || "N/A"}</p>
                </div>
              ) : (
                <>
                  <div className="hp-entrega">
                    <h3 className="hp-subtitle">ORDEN DE ENTREGA NÚMERO {hpSelected.orderNumber}</h3>
                    <div className="hp-row">
                      <p><strong>Fuente:</strong> {hpSelected.fuente || "N/A"}</p>
                      <p><strong>Ejecutiv@:</strong> {hpSelected.ejecutivo || "N/A"}</p>
                    </div>
                    <div className="hp-row">
                      <p><strong>Para:</strong> {hpSelected.para || "N/A"}</p>
                      <p><strong>Teléfono:</strong> {hpSelected.telefono || "N/A"}</p>
                    </div>
                    <p><strong>Entregar En:</strong> {hpSelected.entregarEn || "N/A"}</p>
                    <p><strong>Detalle:</strong> {hpSelected.detalleEntrega || hpSelected.orderItems || "N/A"}</p>
                    <p><strong>Tarjeta:</strong> {hpSelected.tarjeta === "Si" ? (hpSelected.tarjetaDetalle || "Si") : "No"}</p>
                    <p><strong>Recibido por:</strong> {hpSelected.recibidoPor || "N/A"}</p>
                  </div>

                  <div className="hp-hr" aria-hidden="true" />

                  <div className="hp-produccion">
                    <h3 className="hp-subtitle">ORDEN DE PRODUCCIÓN NÚMERO {hpSelected.orderNumber}</h3>
                    <div className="hp-row">
                      <p><strong>Fuente:</strong> {hpSelected.fuente || "N/A"}</p>
                      <p><strong>Ejecutiv@:</strong> {hpSelected.ejecutivo || "N/A"}</p>
                    </div>
                    <div className="hp-row">
                      <p><strong>Para:</strong> {hpSelected.para || "N/A"}</p>
                      <p><strong>Teléfono:</strong> {hpSelected.telefono || "N/A"}</p>
                    </div>
                    <p><strong>Fecha y Hora:</strong> {hpSelected.scheduledTime ? moment(hpSelected.scheduledTime).tz(HP_TZ).format("DD/MM/YYYY h:mma") : "N/A"}</p>
                    <p><strong>Detalle:</strong> {hpSelected.detalleEntrega || hpSelected.orderItems || "N/A"}</p>
                    <p><strong>Parcial:</strong> {hpSelected.total != null ? hpMoney.format(hpSelected.total) : "—"}</p>
                    <p><strong>Transporte:</strong> {hpSelected.transporte != null ? hpMoney.format(hpSelected.transporte) : "—"}</p>
                    <p><strong>IVA:</strong> {hpSelected.iva === "Si" ? "Si (13%)" : "N/A"}</p>
                    <p><strong>Total:</strong> {hpSelected.totalPayment != null ? hpMoney.format(hpSelected.totalPayment) : "—"}</p>
                    <p><strong>Forma de Pago:</strong> {hpSelected.formaPago || "N/A"}</p>
                  </div>
                </>
              )}
            </div>

            <div className="hp-modal-actions">
              <button className="hp-close-btn" onClick={hpCloseModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
